import type { OfferingModel } from 'interfaces/database';
import type * as PI from 'interfaces/project';
import maxObjectSize from './max-object-size';

export const LOW_RESOLUTION_WARNING_ICON_OFFSET = 8;
export const LOW_RESOLUTION_WARNING_ICON_SIZE_DESKTOP = 32;
export const LOW_RESOLUTION_WARNING_ICON_SIZE_MOBILE = 20;

export type PhotoQualityWarningType = 'maxPhotoSize' | 'lowResolution';

interface PhotoWarningObjectModel {
	cropheight?: PI.PageObjectModel['cropheight'];
	cropwidth?: PI.PageObjectModel['cropwidth'];
	height: PI.PageObjectModel['height'];
	maxheight?: PI.PageObjectModel['maxheight'];
	maxwidth?: PI.PageObjectModel['maxwidth'];
	photoid?: PI.PageObjectModel['photoid'];
	type: PI.PageObjectModel['type'];
	width: PI.PageObjectModel['width'];
}

interface PhotoWarningSizeModel {
	height: PI.PhotoModel['full_height'];
	width: PI.PhotoModel['full_width'];
}

interface PhotoWarningTypeOptions {
	objectModel: PhotoWarningObjectModel;
	offeringModel?: OfferingModel | null;
	photoData?: PhotoWarningSizeModel;
}

/**
 * Determines whether quality warnings should be skipped for the current offering.
 *
 * @param offeringModel Offering context used to evaluate warning behavior.
 * @returns True when quality warnings are disabled by offering configuration.
 */
export function shouldSkipPhotoQualityWarnings(offeringModel?: OfferingModel | null): boolean {
	return Boolean(offeringModel?.applyVectorization);
}

/**
 * Resolves the warning icon size based on the viewport mode.
 *
 * @param isMobile Indicates whether the warning is rendered in mobile mode.
 * @returns Icon size in pixels.
 */
export function getLowResolutionWarningIconSize(isMobile: boolean): number {
	return (
		isMobile
			? LOW_RESOLUTION_WARNING_ICON_SIZE_MOBILE
			: LOW_RESOLUTION_WARNING_ICON_SIZE_DESKTOP
	);
}

/**
 * Resolves the photo quality warning type for the provided object.
 *
 * @param options Object, photo, and offering data used to evaluate warning thresholds.
 * @returns The warning type when applicable, otherwise null.
 */
export function getPhotoQualityWarningType(options: PhotoWarningTypeOptions): PhotoQualityWarningType | null {
	if (shouldSkipPhotoQualityWarnings(options.offeringModel)) {
		return null;
	}

	if (
		options.objectModel.type !== 'photo'
		|| !options.objectModel.photoid
	) {
		return null;
	}

	const maxsize = maxObjectSize(
		{
			maxwidth: options.objectModel.maxwidth,
			maxheight: options.objectModel.maxheight,
			cropwidth: options.objectModel.cropwidth,
			cropheight: options.objectModel.cropheight,
			type: options.objectModel.type,
		},
		options.photoData,
		options.offeringModel ?? undefined,
	);

	if (
		Math.round(options.objectModel.width) >= Math.round(maxsize.cropWidth)
		|| Math.round(options.objectModel.height) >= Math.round(maxsize.cropHeight)
	) {
		return 'maxPhotoSize';
	}

	if (
		Math.round(options.objectModel.width) > Math.round(maxsize.croppedQualityWidth)
		|| Math.round(options.objectModel.height) > Math.round(maxsize.croppedQualityHeight)
	) {
		return 'lowResolution';
	}

	return null;
}

/**
 * Determines whether the provided photo object should display a low-resolution warning.
 *
 * @param options Object, photo, and offering data used to evaluate warning thresholds.
 * @returns True when the object is low-resolution.
 */
export function isLowResolutionPhotoObject(options: PhotoWarningTypeOptions): boolean {
	return getPhotoQualityWarningType(options) !== null;
}
